import React, { useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Paper, Stack, Typography,
} from "@mui/material";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { Header } from "../../components/Header";
import RmfDialog, { DialogActions as RmfDialogActions, DialogIcon, DialogText, DialogTitle as RmfDialogTitle, dialogButtonSx } from "../../components/Dialog/RmfDialog";
import { myReservations, cancelMyReservation } from "../../apollo/server";

const MY_RESERVATIONS = gql`${myReservations}`;
const CANCEL = gql`${cancelMyReservation}`;

const ACCENT = "#FF3008";

const STATUS = {
  BOOKED: { label: "Booked", bg: "#fff1ee", color: "#c2410c", border: "#fed7aa" },
  CONFIRMED: { label: "Confirmed", bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  SEATED: { label: "Seated", bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  COMPLETED: { label: "Completed", bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" },
  CANCELLED: { label: "Cancelled", bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" },
  NO_SHOW: { label: "Missed", bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};
const OPEN = new Set(["BOOKED", "CONFIRMED"]);
const errText = (e) => e?.graphQLErrors?.[0]?.message || e?.message || "Something went wrong. Please try again.";

function ReservationCard({ r, onCancel }) {
  const s = STATUS[r.status] || STATUS.BOOKED;
  const when = new Date(r.reservedFor);
  const canCancel = OPEN.has(r.status) && when.getTime() > Date.now();
  return (
    <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e5e7eb", overflow: "hidden", mb: 2 }}>
      <Box sx={{ display: "flex" }}>
        <Box sx={{
          width: { xs: 84, sm: 104 }, flexShrink: 0, bgcolor: "#0f172a", color: "#fff",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2,
        }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6 }}>
            {when.toLocaleDateString("en-US", { month: "short" })}
          </Typography>
          <Typography sx={{ fontSize: "1.9rem", fontWeight: 800, lineHeight: 1 }}>{when.getDate()}</Typography>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, opacity: 0.7 }}>
            {when.toLocaleDateString("en-US", { weekday: "short" })}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2.25, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              {r.restaurant?.slug ? (
                <Typography component={RouterLink} to={`/restaurant/${r.restaurant.slug}`}
                  sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#0f172a", textDecoration: "none", "&:hover": { color: ACCENT } }}>
                  {r.restaurant.name}
                </Typography>
              ) : (
                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>{r.restaurant?.name || "Restaurant"}</Typography>
              )}
              {!!r.restaurant?.address && (
                <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }} noWrap>{r.restaurant.address}</Typography>
              )}
            </Box>
            <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 700 }} />
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 1.25 }}>
            <Stack direction="row" gap={0.6} alignItems="center">
              <AccessTimeRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
                {when.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </Typography>
            </Stack>
            <Stack direction="row" gap={0.6} alignItems="center">
              <GroupRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
                {r.partySize} {r.partySize === 1 ? "person" : "people"}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
              Code <b style={{ color: "#0f172a", letterSpacing: 1 }}>{r.confirmationCode}</b>
            </Typography>
          </Stack>
          {!!r.notes && <Typography sx={{ fontSize: "0.85rem", color: "#475569", mt: 1 }}>“{r.notes}”</Typography>}
          {(canCancel || !!r.restaurant?.phone) && (
            <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
              {canCancel && (
                <Button size="small" variant="outlined" onClick={() => onCancel(r)}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", color: "#b91c1c", borderColor: "#fecaca" }}>
                  Cancel reservation
                </Button>
              )}
              {!!r.restaurant?.phone && OPEN.has(r.status) && (
                <Button size="small" href={`tel:${r.restaurant.phone}`}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", color: "#0f172a" }}>
                  Call {r.restaurant.phone}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default function MyReservations() {
  const { data, loading, refetch } = useQuery(MY_RESERVATIONS, { fetchPolicy: "cache-and-network" });
  const [cancelMut, { loading: cancelling }] = useMutation(CANCEL);
  const [target, setTarget] = useState(null);
  const [error, setError] = useState("");

  const { upcoming, past } = useMemo(() => {
    const list = data?.myReservations || [];
    const now = Date.now();
    const up = list
      .filter((r) => (OPEN.has(r.status) || r.status === "SEATED") && new Date(r.endsAt).getTime() > now)
      .sort((a, b) => new Date(a.reservedFor) - new Date(b.reservedFor));
    const upIds = new Set(up.map((r) => r._id));
    const done = list.filter((r) => !upIds.has(r._id)).sort((a, b) => new Date(b.reservedFor) - new Date(a.reservedFor));
    return { upcoming: up, past: done };
  }, [data]);

  const confirmCancel = async () => {
    setError("");
    try {
      await cancelMut({ variables: { id: target._id } });
      setTarget(null);
      refetch();
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <>
      <Header />
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
        <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #FF3008 130%)", pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 8 } }}>
          <Container maxWidth="lg">
            <Stack direction="row" alignItems="center" gap={2}>
              <Box sx={{
                width: { xs: 52, md: 60 }, height: { xs: 52, md: 60 }, borderRadius: "14px",
                background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <EventSeatRoundedIcon sx={{ color: "#fecaca", fontSize: 28 }} />
              </Box>
              <Box>
                <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", md: "2.1rem" }, color: "#fff", lineHeight: 1.15 }}>
                  My Reservations
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.7)", mt: 0.4 }}>
                  {"Tables you’ve booked at restaurants"}
                </Typography>
              </Box>
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ mt: -4, pb: 6, position: "relative", zIndex: 2 }}>
          {loading && !data ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : !upcoming.length && !past.length ? (
            <Paper elevation={0} sx={{ textAlign: "center", py: 8, px: 3, borderRadius: "16px", border: "1px solid #e5e7eb" }}>
              <EventBusyRoundedIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, mt: 1 }}>No reservations yet</Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#6b7280", mt: 0.5, mb: 2.5 }}>
                {"Look for “Reserve a table” on a restaurant’s page."}
              </Typography>
              <Button component={RouterLink} to="/restaurant-list" variant="contained" disableElevation
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: ACCENT, "&:hover": { bgcolor: "#E02A06" } }}>
                Find a restaurant
              </Button>
            </Paper>
          ) : (
            <>
              <Typography sx={{ fontWeight: 800, color: "#fff", mb: 1.5, fontSize: "0.95rem" }}>
                Upcoming
              </Typography>
              {upcoming.length ? upcoming.map((r) => <ReservationCard key={r._id} r={r} onCancel={(x) => { setError(""); setTarget(x); }} />) : (
                <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e5e7eb", mb: 3 }}>
                  <Typography sx={{ color: "#64748b" }}>Nothing coming up.</Typography>
                </Paper>
              )}
              {past.length > 0 && (
                <>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a", mt: 3, mb: 1.5, fontSize: "0.95rem" }}>Past and cancelled</Typography>
                  {past.map((r) => <ReservationCard key={r._id} r={r} onCancel={() => {}} />)}
                </>
              )}
            </>
          )}
        </Container>
      </Box>

      <RmfDialog open={!!target} onClose={() => !cancelling && setTarget(null)}>
        <DialogIcon tone="red" sx={{ mb: 2.5 }}>
          <EventBusyRoundedIcon />
        </DialogIcon>
        <RmfDialogTitle besideClose={false}>Cancel this reservation?</RmfDialogTitle>
        {target && (
          <DialogText>
            {target.restaurant?.name}, {new Date(target.reservedFor).toLocaleString("en-US", { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}, {target.partySize} {target.partySize === 1 ? "person" : "people"}.
          </DialogText>
        )}
        {!!error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <RmfDialogActions>
          <Box component="button" type="button" onClick={confirmCancel} disabled={cancelling} aria-busy={cancelling} sx={dialogButtonSx.primary}>
            {cancelling ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Cancel reservation"}
          </Box>
          <Box component="button" type="button" onClick={() => setTarget(null)} disabled={cancelling} sx={dialogButtonSx.secondary}>
            Keep it
          </Box>
        </RmfDialogActions>
      </RmfDialog>
    </>
  );
}
